import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, RefreshCw, Mail, User, X, CheckCircle, XCircle, Shield } from 'lucide-react';
import { database, ref, get, set, update, auth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from '../../firebase';
import { ensureAdminAccount } from '../../services/rtdb';

// Dialog Component with updated colors
const Dialog = ({ isOpen, onClose, title, message, type = 'success' }) => {
  if (!isOpen) return null;

  const getDialogStyles = () => {
    const bgColor = '#132131';
    const textColor = 'text-white';
    
    switch (type) {
      case 'error':
        return {
          bg: 'bg-[#132131]',
          border: 'border-red-500/30',
          icon: <XCircle className="text-red-400" size={40} />,
          titleColor: `${textColor}`,
          textColor: 'text-gray-200'
        };
      case 'warning':
        return {
          bg: 'bg-[#132131]',
          border: 'border-yellow-500/30',
          icon: <div className="text-yellow-400 text-xl">⚠️</div>,
          titleColor: `${textColor}`,
          textColor: 'text-gray-200'
        };
      default: // success
        return {
          bg: 'bg-[#132131]',
          border: 'border-green-500/30',
          icon: <CheckCircle className="text-green-400" size={40} />,
          titleColor: `${textColor}`,
          textColor: 'text-gray-200'
        };
    }
  };

  const styles = getDialogStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className={`relative z-10 w-full max-w-sm rounded-xl ${styles.bg} border ${styles.border} p-6 shadow-2xl`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>

        {/* Content */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {styles.icon}
          </div>
          
          <h3 className={`text-xl font-bold mb-3 font-heading ${styles.titleColor}`}>
            {title}
          </h3>
          
          <div className={`text-sm mb-6 font-body ${styles.textColor} whitespace-pre-line`}>
            {message}
          </div>
          
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg font-heading font-medium text-sm transition-all ${
              type === 'success' 
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white' 
                : type === 'error'
                ? 'bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white'
                : 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white'
            }`}
          >
            {type === 'success' ? 'Continue' : 'Try Again'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Location Service with production-safe API calls
const getCurrentLocation = () => {
  return new Promise(async (resolve, reject) => {
    try {
      // Try browser geolocation first (most accurate)
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0
            });
          });

          const { latitude, longitude } = position.coords;
          
          // Try reverse geocoding with a production-safe API
          try {
            // Use a free, CORS-friendly geocoding API
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
            );
            const data = await response.json();
            
            const locationData = {
              ipAddress: 'Browser GPS',
              country: data.address?.country || 'Unknown',
              countryCode: data.address?.country_code?.toUpperCase() || '',
              region: data.address?.state || data.address?.region || 'Unknown',
              city: data.address?.city || data.address?.town || data.address?.village || 'Unknown',
              gpsLatitude: latitude,
              gpsLongitude: longitude,
              gpsAccuracy: position.coords.accuracy,
              locationSource: 'GPS + Reverse Geocoding',
              isGPSAvailable: true,
              timestamp: new Date().toISOString()
            };
            
            console.log('📍 GPS location obtained:', locationData.city, locationData.country);
            resolve(locationData);
            return;
          } catch (reverseError) {
            // If reverse geocoding fails, return just GPS coordinates
            const locationData = {
              ipAddress: 'Browser GPS',
              country: 'Unknown',
              countryCode: '',
              region: 'Unknown',
              city: 'Unknown',
              gpsLatitude: latitude,
              gpsLongitude: longitude,
              gpsAccuracy: position.coords.accuracy,
              locationSource: 'GPS Only',
              isGPSAvailable: true,
              timestamp: new Date().toISOString()
            };
            resolve(locationData);
            return;
          }
        } catch (gpsError) {
          console.warn('GPS location failed:', gpsError.message);
          // Fall through to IP-based location
        }
      }

      // Fallback to IP-based location (production-safe)
      try {
        // Use ipapi.co which is free and CORS-friendly
        const response = await fetch('https://ipapi.co/json/');
        if (response.ok) {
          const data = await response.json();
          const locationData = {
            ipAddress: data.ip || 'Unknown',
            country: data.country_name || 'Unknown',
            countryCode: data.country_code || '',
            region: data.region || 'Unknown',
            city: data.city || 'Unknown',
            latitude: data.latitude || null,
            longitude: data.longitude || null,
            postalCode: data.postal || '',
            timezone: data.timezone || 'Unknown',
            isp: data.org || 'Unknown',
            locationSource: 'IP Geolocation (ipapi.co)',
            timestamp: new Date().toISOString()
          };
          console.log('📍 IP location obtained:', locationData.city, locationData.country);
          resolve(locationData);
          return;
        }
      } catch (ipError) {
        console.warn('IP location failed:', ipError);
      }

      // Final fallback
      const fallbackLocation = {
        ipAddress: 'Unknown',
        country: 'Unknown',
        countryCode: '',
        region: 'Unknown',
        city: 'Unknown',
        latitude: 0,
        longitude: 0,
        postalCode: '',
        locationSource: 'Fallback',
        timestamp: new Date().toISOString()
      };
      resolve(fallbackLocation);
      
    } catch (error) {
      console.error('Location service error:', error);
      const fallbackLocation = {
        ipAddress: 'Failed to fetch',
        country: 'Unknown',
        countryCode: '',
        region: 'Unknown',
        city: 'Unknown',
        latitude: 0,
        longitude: 0,
        postalCode: '',
        locationSource: 'Fallback',
        timestamp: new Date().toISOString()
      };
      resolve(fallbackLocation);
    }
  });
};

const Login = () => {
  const location = useLocation();
  const isAdminPath = location.pathname === '/admin-login';
  
  const [form, setForm] = useState({ 
    userId: '', 
    password: '', 
    captcha: '' 
  });
  const [adminConfig, setAdminConfig] = useState({
    loaded: false,
    passwordEmpty: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [loginLocation, setLoginLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const navigate = useNavigate();

  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'success'
  });

  // CAPTCHA Logic
  const generateCaptcha = () => {
    let captcha = '';
    for (let i = 0; i < 6; i++) {
      captcha += Math.floor(Math.random() * 10);
    }
    return captcha;
  };

  const [captchaText, setCaptchaText] = useState(generateCaptcha());

  const reloadCaptcha = () => {
    setCaptchaText(generateCaptcha());
    setForm({ ...form, captcha: '' });
  };

  // Show dialog function
  const showDialog = (title, message, type = 'success') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  // Close dialog function
  const closeDialog = () => {
    setDialog({
      isOpen: false,
      title: '',
      message: '',
      type: 'success'
    });
  };

  // Initialize location on mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        setLocationLoading(true);
        setLocationError('');
        
        const locationData = await getCurrentLocation();
        setLoginLocation(locationData);
        
        console.log('📍 Login location detected:', locationData);
      } catch (error) {
        console.error('Failed to get location:', error);
        setLocationError('Location detection failed. Login will proceed without location data.');
      } finally {
        setLocationLoading(false);
      }
    };

    const initAdmin = async () => {
      if (!isAdminPath) return;
      try {
        const today = new Date().toISOString().split('T')[0];
        let admin = null;

        // helper that tries both `admin/${date}` and `admin/admin/${date}`
        const loadAdminForDate = async (dateKey) => {
          const pathA = `admin/${dateKey}/adminAccount`;
          const pathB = `admin/admin/${dateKey}/adminAccount`;
          let snap = await get(ref(database, pathA));
          if (snap.exists()) return { data: snap.val(), path: pathA };
          snap = await get(ref(database, pathB));
          if (snap.exists()) return { data: snap.val(), path: pathB };
          return { data: null, path: pathA };
        };

        const { data, path } = await loadAdminForDate(today);
        if (data) {
          admin = data;
          // if the record was in the `admin/admin` variant, migrate it outward
          if (path.includes('/admin/')) {
            await set(ref(database, `admin/${today}/adminAccount`), admin);
          }
        } else {
          // try root locations
          const rootA = await get(ref(database, 'admin/adminAccount'));
          if (rootA.exists()) {
            admin = rootA.val();
            // also mirror into today's folder
            await set(ref(database, `admin/${today}/adminAccount`), admin);
          } else {
            const rootB = await get(ref(database, 'admin/admin/adminAccount'));
            if (rootB.exists()) {
              admin = rootB.val();
              await set(ref(database, `admin/${today}/adminAccount`), admin);
            }
          }
        }

        // if still missing, create skeleton in today's folder
        if (!admin) {
          admin = await ensureAdminAccount(today);
        }

        setAdminConfig({ loaded: true, passwordEmpty: !admin.password });

        // ENHANCED: Ensure Firebase UID exists for admin
        if (admin && admin.email && admin.password) {
          try {
            let firebaseUid = admin.firebaseUid || '';
            
            // If no Firebase UID exists, try to create/get one
            if (!firebaseUid) {
              console.log('🔑 No Firebase UID found for admin, attempting to create...');
              
              try {
                // Try to sign in first
                await signInWithEmailAndPassword(auth, admin.email, admin.password);
                firebaseUid = auth.currentUser?.uid || '';
                console.log('✅ Admin signed in, UID:', firebaseUid);
              } catch (signErr) {
                if (signErr.code === 'auth/user-not-found') {
                  // User doesn't exist, create new account
                  try {
                    const cred = await createUserWithEmailAndPassword(auth, admin.email, admin.password);
                    firebaseUid = cred.user?.uid || '';
                    console.log('✅ Admin Firebase account created with UID:', firebaseUid);
                  } catch (createErr) {
                    console.error('❌ Failed to create admin Firebase account:', createErr);
                  }
                } else {
                  console.warn('⚠️ Admin Firebase sign-in error:', signErr.code);
                }
              }
              
              // Save the Firebase UID to database if we got one
              if (firebaseUid) {
                await update(ref(database, `admin/${today}/adminAccount`), { firebaseUid });
                await update(ref(database, `admin/adminAccount`), { firebaseUid });
                console.log('✅ Firebase UID saved to admin records:', firebaseUid);
              }
            } else {
              console.log('✅ Admin already has Firebase UID:', firebaseUid);
            }
          } catch (uidErr) {
            console.warn('⚠️ Error ensuring admin Firebase UID:', uidErr);
          }
        }

        // For admin login, prefill with email instead of userId
        if (admin.email) {
          setForm(f => ({ ...f, userId: admin.email }));
        } else if (admin.userId) {
          setForm(f => ({ ...f, userId: admin.userId }));
        }
      } catch (err) {
        console.warn('Admin initialization failed', err);
        setAdminConfig({ loaded: true, passwordEmpty: false });
      }
    };
    
    fetchLocation();
    initAdmin();
  }, [isAdminPath]);

  // Ensure an admin auth user exists and return its UID (creates user if needed)
  const ensureAdminFirebaseUid = async (email, password, today) => {
    if (!email || !password) return '';
    
    let uid = '';
    
    try {
      // First try to sign in
      await signInWithEmailAndPassword(auth, email, password);
      uid = auth.currentUser?.uid || '';
      console.log('🔑 Admin signed in, UID:', uid);
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        // User doesn't exist, create new account
        try {
          const cred = await createUserWithEmailAndPassword(auth, email, password);
          uid = cred.user?.uid || '';
          console.log('🔑 Admin Firebase account created with UID:', uid);
        } catch (createErr) {
          console.error('❌ Failed to create admin Firebase account:', createErr);
        }
      } else if (err.code === 'auth/invalid-email') {
        console.error('❌ Invalid admin email format:', email);
      } else {
        console.warn('⚠️ Admin Firebase auth error:', err.code);
      }
    }

    if (uid) {
      try {
        // Save UID to both locations
        await update(ref(database, `admin/${today}/adminAccount`), { firebaseUid: uid });
        await update(ref(database, `admin/adminAccount`), { firebaseUid: uid });
        console.log('✅ Firebase UID persisted to admin records');
      } catch (dbErr) {
        console.error('❌ Failed to persist admin Firebase UID:', dbErr);
      }
    }

    return uid;
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!forgotEmail.trim()) {
      showDialog('Email Required', 'Please enter your registered email address', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      showDialog('Invalid Email', 'Please enter a valid email address', 'error');
      return;
    }

    setForgotLoading(true);

    try {
      // Check if email exists in all date folders
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      let userFound = false;
      let userData = null;

      if (snapshot.exists()) {
        const usersByDate = snapshot.val();
        
        // Search through all date folders
        for (const date in usersByDate) {
          const users = usersByDate[date];
          
          for (const [key, data] of Object.entries(users)) {
            if (data.email && data.email.toLowerCase() === forgotEmail.toLowerCase()) {
              userFound = true;
              userData = data;
              break;
            }
          }
          if (userFound) break;
        }
      }

      if (!userFound) {
        showDialog('Email Not Found', 'Email not found. Please check your email address.', 'error');
        setForgotLoading(false);
        return;
      }

      // Generate reset token
      const resetToken = Date.now().toString(36) + Math.random().toString(36).substr(2);
      
      // Save reset token
      await set(ref(database, 'passwordResets/' + resetToken), {
        email: forgotEmail,
        userId: userData.sponsorId,
        userName: userData.name,
        requestedTime: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        used: false
      });

      showDialog('Password Reset', 'Password reset email has been sent to your registered email address.', 'success');
      setForgotEmail('');
      setShowForgotPassword(false);

    } catch (error) {
      console.error('Error sending reset email:', error);
      showDialog('Error', 'Error sending reset email. Please try again.', 'error');
    } finally {
      setForgotLoading(false);
    }
  };

  // Save admin login log with session tracking
  const saveAdminLoginLog = async (sessionId) => {
    try {
      const loginTime = new Date().toISOString();
      const currentDate = new Date().toISOString().split('T')[0];
      
      let locationData = loginLocation;
      if (!locationData) {
        try {
          locationData = await getCurrentLocation();
        } catch (error) {
          locationData = {
            ipAddress: 'Failed to fetch',
            country: 'Unknown',
            city: 'Unknown'
          };
        }
      }

      const cleanLocation = {
        ipAddress: locationData.ipAddress || '',
        country: locationData.country || 'Unknown',
        city: locationData.city || 'Unknown',
        region: locationData.region || '',
        // prefer GPS coordinates if available (from browser)
        latitude: locationData.gpsLatitude ?? locationData.latitude ?? 0,
        longitude: locationData.gpsLongitude ?? locationData.longitude ?? 0
      };

      const adminLog = {
        userId: 'admin',
        userName: 'Administrator',
        loginTime: loginTime,
        location: cleanLocation,
        userAgent: navigator.userAgent,
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
        status: 'success',
        timestamp: loginTime,
        sessionId: sessionId,
        sessionExpiresAt: Date.now() + (20 * 60 * 1000)
      };
      // if we happen to have a firebase uid on the sessionId value, include it
      if (sessionId && sessionId.startsWith('session_') === false) {
        adminLog.firebaseUid = sessionId;
      }

      // Save to date-wise structure
      const adminDateRef = ref(database, `admin/${currentDate}/logs`);
      const snapshot = await get(adminDateRef);
      
      let logCount = 0;
      if (snapshot.exists()) {
        const logs = snapshot.val();
        logCount = Object.keys(logs).length;
      }
      
      const logKey = logCount.toString();
      await set(ref(database, `admin/${currentDate}/logs/${logKey}`), adminLog);
      
      console.log(`✅ Admin login log saved with session ID: ${sessionId}`);
    } catch (error) {
      console.error('Error saving admin login log:', error);
    }
  };

  // Save user login log
  const saveUserLoginLog = async (userId, userName, dbKey, registrationDate) => {
    try {
      const loginTime = new Date().toISOString();
      
      let locationData = loginLocation;
      if (!locationData) {
        try {
          locationData = await getCurrentLocation();
          setLoginLocation(locationData);
        } catch (error) {
          locationData = {
            ipAddress: 'Failed to fetch',
            country: 'Unknown',
            city: 'Unknown'
          };
        }
      }

      const cleanLocation = {
        ipAddress: locationData.ipAddress || '',
        country: locationData.country || 'Unknown',
        city: locationData.city || 'Unknown',
        region: locationData.region || '',
        latitude: locationData.gpsLatitude ?? locationData.latitude ?? 0,
        longitude: locationData.gpsLongitude ?? locationData.longitude ?? 0
      };

      const loginLog = {
        userId,
        userName,
        loginTime,
        location: cleanLocation,
        userAgent: navigator.userAgent,
        deviceType: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
        status: 'success',
        timestamp: loginTime
      };

      const registrationDateStr = registrationDate ? 
        new Date(registrationDate).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0];
      
      const usersRef = ref(database, `users/${registrationDateStr}`);
      const snapshot = await get(usersRef);
      
      if (snapshot.exists()) {
        const users = snapshot.val();
        const userData = users[dbKey];
        
        if (userData) {
          const loginHistory = userData.loginHistory || [];
          
          loginHistory.unshift({
            ...loginLog,
            id: Date.now().toString()
          });
          
          if (loginHistory.length > 50) {
            loginHistory.length = 50;
          }
          
          await update(ref(database, `users/${registrationDateStr}/${dbKey}`), {
            lastLogin: loginTime,
            loginHistory: loginHistory,
            lastLoginLocation: cleanLocation
          });
        }
      }
    } catch (error) {
      console.error('Error saving login log:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // CAPTCHA Validation
    if (form.captcha !== captchaText) {
      showDialog('Invalid CAPTCHA', 'Invalid CAPTCHA. Please try again.', 'error');
      reloadCaptcha();
      return;
    }

    // require password only if admin account already has one set
    if (!form.userId || (!form.password && !(isAdminPath && adminConfig.passwordEmpty))) {
      showDialog('Required Fields', 'Please enter User ID/Email and Password', 'error');
      return;
    }

    setIsLoading(true);

    try {
      if (isAdminPath) {
        // Admin Login - fetch or initialize admin account
        const today = new Date().toISOString().split('T')[0];
        let adminData = null;
        let adminPath = '';

        // Search for admin account by email (since that's what's in the database)
        // First check today's folder
        const todayAdminRef = ref(database, `admin/${today}/adminAccount`);
        const todaySnapshot = await get(todayAdminRef);
        
        if (todaySnapshot.exists()) {
          const data = todaySnapshot.val();
          // Check if the email matches
          if (data.email && data.email.toLowerCase() === form.userId.toLowerCase()) {
            adminData = data;
            adminPath = `admin/${today}/adminAccount`;
          }
        }

        // If not found in today's folder, check root admin location
        if (!adminData) {
          const rootAdminRef = ref(database, 'admin/adminAccount');
          const rootSnapshot = await get(rootAdminRef);
          
          if (rootSnapshot.exists()) {
            const data = rootSnapshot.val();
            if (data.email && data.email.toLowerCase() === form.userId.toLowerCase()) {
              adminData = data;
              adminPath = 'admin/adminAccount';
              // Mirror to today's folder
              await set(ref(database, `admin/${today}/adminAccount`), data);
            }
          }
        }

        // If still not found, try the nested path
        if (!adminData) {
          const nestedAdminRef = ref(database, 'admin/admin/adminAccount');
          const nestedSnapshot = await get(nestedAdminRef);
          
          if (nestedSnapshot.exists()) {
            const data = nestedSnapshot.val();
            if (data.email && data.email.toLowerCase() === form.userId.toLowerCase()) {
              adminData = data;
              adminPath = 'admin/admin/adminAccount';
              // Mirror to today's folder
              await set(ref(database, `admin/${today}/adminAccount`), data);
            }
          }
        }

        // Deep-search the entire admin tree for a matching account (e.g. earlier date folders)
        if (!adminData) {
          const fullSnap = await get(ref(database, 'admin'));
          if (fullSnap.exists()) {
            const searchObj = fullSnap.val();
            const findMatching = (obj, currentPath = '') => {
              if (!obj || typeof obj !== 'object') return null;
              if (obj.adminAccount && typeof obj.adminAccount === 'object') {
                const candidate = obj.adminAccount;
                if (candidate.email && candidate.email.toLowerCase() === form.userId.toLowerCase()) {
                  return { data: candidate, path: currentPath ? `${currentPath}/adminAccount` : 'adminAccount' };
                }
              }
              for (const key of Object.keys(obj)) {
                if (typeof obj[key] === 'object') {
                  const newPath = currentPath ? `${currentPath}/${key}` : key;
                  const res = findMatching(obj[key], newPath);
                  if (res) return res;
                }
              }
              return null;
            };
            const found = findMatching(searchObj);
            if (found) {
              console.log('✅ Deep search located admin record for', form.userId);
              adminData = found.data;
              adminPath = found.path;
              await set(ref(database, `admin/${today}/adminAccount`), adminData);
            }
          }
        }

        // If still no admin found, create skeleton (but this shouldn't happen with your data)
        if (!adminData) {
          const skeleton = await ensureAdminAccount(today);
          // Check if the email matches
          if (skeleton.email && skeleton.email.toLowerCase() === form.userId.toLowerCase()) {
            adminData = skeleton;
            adminPath = `admin/${today}/adminAccount`;
          } else {
            showDialog('Admin Not Found', '❌ Admin account not found with this email. Please check and try again.', 'error');
            setIsLoading(false);
            reloadCaptcha();
            return;
          }
        }

        // Password check
        if (adminData.password !== form.password) {
          showDialog('Incorrect Password', '❌ Incorrect admin password. Please try again.', 'error');
          setIsLoading(false);
          reloadCaptcha();
          return;
        }

        // ENHANCED: Ensure Firebase UID is generated/retrieved during login
        let adminUid = adminData.firebaseUid || '';
        
        if (!adminUid) {
          console.log('🔑 No Firebase UID found for admin during login, attempting to create...');
          
          try {
            // Try to sign in first
            await signInWithEmailAndPassword(auth, adminData.email, form.password);
            adminUid = auth.currentUser?.uid || '';
            console.log('✅ Admin signed in, UID:', adminUid);
          } catch (signErr) {
            if (signErr.code === 'auth/user-not-found') {
              // User doesn't exist, create new account
              try {
                const cred = await createUserWithEmailAndPassword(auth, adminData.email, form.password);
                adminUid = cred.user?.uid || '';
                console.log('✅ Admin Firebase account created with UID:', adminUid);
              } catch (createErr) {
                console.error('❌ Failed to create admin Firebase account:', createErr);
              }
            } else if (signErr.code === 'auth/wrong-password') {
              console.warn('⚠️ Wrong password for existing admin Firebase account');
              // Still proceed with login, but note the UID might be retrievable
              try {
                // We might still be able to get the UID from the error
                if (signErr.customData?.uid) {
                  adminUid = signErr.customData.uid;
                }
              } catch (e) {}
            } else {
              console.warn('⚠️ Admin Firebase auth error:', signErr.code);
            }
          }
          
          // If we still don't have a UID, try to get current user
          if (!adminUid && auth.currentUser) {
            adminUid = auth.currentUser.uid;
          }
          
          // Save the Firebase UID to database if we got one
          if (adminUid) {
            const updates = {};
            updates[`admin/${today}/adminAccount/firebaseUid`] = adminUid;
            updates['admin/adminAccount/firebaseUid'] = adminUid;
            
            if (adminPath && adminPath !== `admin/${today}/adminAccount` && adminPath !== 'admin/adminAccount') {
              updates[`${adminPath}/firebaseUid`] = adminUid;
            }
            
            await update(ref(database), updates);
            console.log('✅ Firebase UID saved to admin records:', adminUid);
          }
        } else {
          console.log('✅ Admin already has Firebase UID:', adminUid);
          
          // Verify the Firebase UID is still valid
          try {
            if (auth.currentUser) {
              if (auth.currentUser.uid !== adminUid) {
                console.warn('⚠️ Stored Firebase UID mismatch with current user');
              }
            } else {
              // Try to sign in to refresh the session
              await signInWithEmailAndPassword(auth, adminData.email, form.password);
              console.log('✅ Admin Firebase session refreshed');
            }
          } catch (verifyErr) {
            console.warn('⚠️ Could not verify admin Firebase UID:', verifyErr);
          }
        }

        // Store Firebase UID in localStorage
        if (adminUid) {
          localStorage.setItem('firebaseUid', adminUid);
        } else {
          console.warn('⚠️ Admin login succeeded but no Firebase UID was obtained');
          // Generate a session ID as fallback
          adminUid = `session_${Date.now()}`;
        }

        // Log the admin login
        try {
          await saveAdminLoginLog(adminUid);
        } catch (logErr) {
          console.error('Failed to record admin login log', logErr);
        }

        // Final check for Firebase UID
        if (!adminData.firebaseUid && !adminUid) {
          console.warn('🔍 adminData.firebaseUid missing even after login, please investigate');
        } else {
          console.log('✅ Admin login complete with Firebase UID:', adminUid);
        }

        // Build welcome message with location if available
        let adminMsg = '✅ Welcome, Administrator!';
        if (loginLocation) {
          const lat = loginLocation.gpsLatitude ?? loginLocation.latitude;
          const lon = loginLocation.gpsLongitude ?? loginLocation.longitude;
          if (lat != null && lon != null) {
            adminMsg += ` (lat:${lat.toFixed(4)}, lon:${lon.toFixed(4)})`;
          }
        }

        showDialog('Welcome Admin', adminMsg, 'success');
        localStorage.setItem('userRole', 'admin');
        localStorage.setItem(
          'aerovoltAuth',
          JSON.stringify({ 
            role: 'admin', 
            userId: adminData?.userId || 'admin', 
            userName: 'Administrator',
            firebaseUid: adminUid 
          })
        );
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
        
      } else {
        // Regular User Login
        let userFound = null;
        let dbKey = '';
        let registrationDate = '';
        const normalizedInputUserId = form.userId.toLowerCase();

        // Fast path: try the last known folder first
        const cachedRegistrationDate = localStorage.getItem('registrationDate');
        const cachedDbKey = localStorage.getItem('dbKey');
        if (cachedRegistrationDate && cachedDbKey) {
          const cachedUserRef = ref(database, `users/${cachedRegistrationDate}/${cachedDbKey}`);
          const cachedUserSnapshot = await get(cachedUserRef);
          if (cachedUserSnapshot.exists()) {
            const cachedUser = cachedUserSnapshot.val();
            if (cachedUser?.sponsorId && cachedUser.sponsorId.toLowerCase() === normalizedInputUserId) {
              userFound = cachedUser;
              dbKey = cachedDbKey;
              registrationDate = cachedRegistrationDate;
            }
          }
        }

        if (!userFound) {
          const usersRef = ref(database, 'users');
          const snapshot = await get(usersRef);

          if (snapshot.exists()) {
            const usersByDate = snapshot.val();
            
            for (const date in usersByDate) {
              const users = usersByDate[date];
              
              for (const [key, userData] of Object.entries(users)) {
                if (userData.sponsorId && userData.sponsorId.toLowerCase() === normalizedInputUserId) {
                  userFound = userData;
                  dbKey = key;
                  registrationDate = date;
                  break;
                }
              }
              if (userFound) break;
            }
          }
        }
        
        if (userFound) {
          // CHECK IF USER IS BLOCKED
          if (userFound.loginStatus === 'blocked') {
            showDialog('Account Blocked', 'Your account has been blocked. Please contact admin for support.', 'error');
            reloadCaptcha();
            setIsLoading(false);
            return;
          }

          // Check password
          if (userFound.password === form.password) {
            // ENHANCED: Ensure Firebase UID is generated/retrieved for regular users
            let userUid = userFound.firebaseUid || '';
            
            if (!userUid && userFound.email) {
              console.log('🔑 No Firebase UID found for user, attempting to create...');
              
              try {
                // Try to sign in first
                await signInWithEmailAndPassword(auth, userFound.email, form.password);
                userUid = auth.currentUser?.uid || '';
                console.log('✅ User signed in, UID:', userUid);
              } catch (signErr) {
                if (signErr.code === 'auth/user-not-found') {
                  // User doesn't exist, create new account
                  try {
                    const cred = await createUserWithEmailAndPassword(auth, userFound.email, form.password);
                    userUid = cred.user?.uid || '';
                    console.log('✅ User Firebase account created with UID:', userUid);
                  } catch (createErr) {
                    console.error('❌ Failed to create user Firebase account:', createErr);
                  }
                } else if (signErr.code === 'auth/wrong-password') {
                  console.warn('⚠️ Wrong password for existing user Firebase account');
                  // We might still be able to get the UID from the error
                  if (signErr.customData?.uid) {
                    userUid = signErr.customData.uid;
                  }
                } else {
                  console.warn('⚠️ User Firebase auth error:', signErr.code);
                }
              }
              
              // If we still don't have a UID, try to get current user
              if (!userUid && auth.currentUser) {
                userUid = auth.currentUser.uid;
              }
              
              // Save the Firebase UID to database if we got one
              if (userUid) {
                try {
                  await update(ref(database, `users/${registrationDate}/${dbKey}`), { 
                    firebaseUid: userUid,
                    lastFirebaseSync: new Date().toISOString()
                  });
                  console.log('✅ Firebase UID saved for user:', userUid);
                } catch (updateErr) {
                  console.error('❌ Failed to save user Firebase UID:', updateErr);
                }
              }
            } else if (userFound.firebaseUid) {
              console.log('✅ User already has Firebase UID:', userFound.firebaseUid);
              userUid = userFound.firebaseUid;
              
              // Verify the Firebase UID is still valid
              try {
                if (auth.currentUser) {
                  if (auth.currentUser.uid !== userUid) {
                    console.warn('⚠️ Stored Firebase UID mismatch with current user');
                  }
                } else {
                  // Try to sign in to refresh the session
                  await signInWithEmailAndPassword(auth, userFound.email, form.password);
                  console.log('✅ User Firebase session refreshed');
                }
              } catch (verifyErr) {
                console.warn('⚠️ Could not verify user Firebase UID:', verifyErr);
              }
            }

            // Store Firebase UID in localStorage
            if (userUid) {
              localStorage.setItem('firebaseUid', userUid);
            } else {
              console.warn('⚠️ User login succeeded but no Firebase UID was obtained');
            }

            // Store user info
            localStorage.setItem('userRole', userFound.role || 'user');
            localStorage.setItem('userId', userFound.sponsorId);
            localStorage.setItem('userName', userFound.name);
            localStorage.setItem('userEmail', userFound.email || '');
            localStorage.setItem('userCountry', userFound.country || 'N/A');
            localStorage.setItem('userMobile', userFound.mobile);
            localStorage.setItem('userSponsorId', userFound.sponsorId);
            localStorage.setItem('userBalance', userFound.fund?.balance || 0);
            localStorage.setItem('dbKey', dbKey);
            localStorage.setItem('registrationDate', registrationDate);
            localStorage.setItem(
              'aerovoltAuth',
              JSON.stringify({
                role: userFound.role || 'user',
                userId: userFound.sponsorId,
                userName: userFound.name,
                dbKey,
                registrationDate,
                firebaseUid: userUid
              })
            );
            
            // Save login log
            Promise.resolve(saveUserLoginLog(userFound.sponsorId, userFound.name, dbKey, userFound.registrationDate)).catch(console.error);
            
            let locationInfo = '';
            if (loginLocation) {
              const cityGood = loginLocation.city && loginLocation.city !== 'Unknown';
              const countryGood = loginLocation.country && loginLocation.country !== 'Unknown';
              if (cityGood || countryGood) {
                locationInfo = ` from ${cityGood ? loginLocation.city : ''}${cityGood && countryGood ? ', ' : ''}${countryGood ? loginLocation.country : ''}`;
              }
              if (loginLocation.gpsLatitude != null && loginLocation.gpsLongitude != null) {
                locationInfo += ` (${loginLocation.gpsLatitude.toFixed(4)}, ${loginLocation.gpsLongitude.toFixed(4)})`;
              }
            }
            
            // Final check for Firebase UID
            if (!userFound.firebaseUid && !userUid) {
              console.warn('🔍 User record and localStorage both lack Firebase UID after login for', userFound.sponsorId);
            } else {
              console.log('✅ User login complete with Firebase UID:', userUid || 'none');
            }
            
            showDialog('Welcome Back', `✅ Welcome back, ${userFound.name}!${locationInfo}`, 'success');
            
            setTimeout(() => {
              navigate('/user-dashboard');
            }, 1500);
            
          } else {
            showDialog('Incorrect Password', '❌ Incorrect password. Please try again.', 'error');
            reloadCaptcha();
          }
        } else {
          showDialog('User Not Found', '❌ User ID not found. Please register first.', 'error');
          reloadCaptcha();
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showDialog('Login Failed', 'Login failed. Please try again.', 'error');
      reloadCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  // Handle CAPTCHA input
  const handleCaptchaChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '');
    setForm({ ...form, captcha: digitsOnly });
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center pt-16 md:pt-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Dialog Component */}
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-card p-5 md:p-7 backdrop-blur-lg card-glow">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 md:mb-6">
            <div className="mb-3 md:mb-0 flex items-center gap-2">
              {isAdminPath ? (
                <Shield className="h-6 w-6 text-primary" />
              ) : null}
              <h1 className="text-xl md:text-2xl font-bold text-foreground font-heading">
                {isAdminPath ? 'Admin Login' : 'User Login'}
              </h1>
            </div>
            <div className="text-left md:text-right">
              <p className="text-sm md:text-base text-muted-foreground font-body">
                {isAdminPath ? 'Secure administrator access' : 'Sign in to your user account'}
              </p>
            </div>
          </div>
          
          {!showForgotPassword ? (
            <>
              <form onSubmit={handleLogin} className="space-y-3 md:space-y-4 mt-4 md:mt-0">
                {/* User ID and Password Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {/* User ID Field - For admin, this will accept email */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading">
                      {isAdminPath ? 'Admin Email *' : 'Sponsor ID (Login ID) *'}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        autoComplete="off"
                        placeholder={isAdminPath ? "Enter admin email" : "Enter your Sponsor ID"}
                        value={form.userId}
                        onChange={(e) => setForm({ ...form, userId: e.target.value })}
                        required
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 md:pr-12 font-body"
                      />
                      {isAdminPath ? (
                        <Shield className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                      ) : (
                        <User className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                      )}
                    </div>
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading">Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete={isAdminPath ? (adminConfig.passwordEmpty ? 'new-password' : 'current-password') : 'current-password'}
                        placeholder={isAdminPath && adminConfig.passwordEmpty ?
                          "(no password yet - leave blank)" :
                          "Enter your password"
                        }
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required={!(isAdminPath && adminConfig.passwordEmpty)}
                        className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 md:pr-12 font-body"
                      />
                      <button 
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* CAPTCHA Section */}
                <div className="space-y-2">
                  <label className="block text-xs md:text-sm font-medium font-heading">CAPTCHA Verification *</label>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between bg-secondary/80 rounded-lg p-2.5 md:p-3 border border-border card-glow gap-2 sm:gap-0">
                    <span className="font-mono text-lg md:text-xl tracking-widest select-none text-foreground bg-white/10 px-2 md:px-3 py-1.5 rounded font-body text-center sm:text-left">
                      {captchaText}
                    </span>
                    <button
                      type="button"
                      onClick={reloadCaptcha}
                      className="text-primary hover:text-primary/80 flex items-center justify-center gap-1.5 px-2.5 md:px-3 py-1.5 bg-secondary rounded hover:bg-secondary/80 transition-colors font-body text-xs md:text-sm"
                    >
                      <RefreshCw size={14} />
                      <span className="whitespace-nowrap">Reload CAPTCHA</span>
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Enter the 6-digit CAPTCHA code above"
                      value={form.captcha}
                      onChange={(e) => handleCaptchaChange(e.target.value)}
                      maxLength={6}
                      required
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none font-body"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-body">
                      {form.captcha.length}/6
                    </div>
                  </div>
                </div>

                {/* Login Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`btn-electric w-full py-2.5 md:py-3 text-sm md:text-base font-medium rounded-lg ${
                    isLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? 'Signing In...' : isAdminPath ? 'Admin Sign In' : 'Sign In'}
                </button>
              </form>

              {/* Forgot Password & Register Now (only for user login) */}
              {!isAdminPath && (
                <div className="mt-3 md:mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-primary hover:text-primary/80 font-medium text-xs md:text-sm flex items-center justify-center sm:justify-start gap-1.5 font-body"
                  >
                    <Mail size={14} />
                    Forgot Password?
                  </button>
                  
                  <div className="text-center sm:text-right">
                    <span className="text-xs md:text-sm text-muted-foreground font-body">
                      Don't have an account?{' '}
                      <Link to="/register" className="text-primary font-medium hover:underline whitespace-nowrap">
                        Register Now
                      </Link>
                    </span>
                  </div>
                </div>
              )}

              {/* Switch between Admin/User Login */}
              {/* <div className="text-center mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground font-body">
                  {isAdminPath ? (
                    <>Regular user? <Link to="/login" className="text-primary font-medium hover:underline">Click here for User Login</Link></>
                  ) : (
                    <>Admin? <Link to="/admin-login" className="text-primary font-medium hover:underline">Click here for Admin Login</Link></>
                  )}
                </p>
              </div> */}

              {/* Terms & Policies Links */}
              <div className="text-center mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border">
                <div className="flex flex-wrap justify-center gap-2 md:gap-4 text-xs text-muted-foreground font-body">
                  <Link to="/terms" className="hover:text-primary hover:underline whitespace-nowrap">
                    Terms & Conditions
                  </Link>
                  <Link to="/privacy" className="hover:text-primary hover:underline whitespace-nowrap">
                    Privacy Policy
                  </Link>
                  {/* <Link to="/refund" className="hover:text-primary hover:underline whitespace-nowrap">
                    Refund Policy
                  </Link> */}
                </div>
                
                {/* Back to Home */}
                <div className="text-center mt-2 md:mt-3">
                  <Link to="/" className="text-xs text-muted-foreground hover:text-primary hover:underline font-body flex items-center justify-center gap-1">
                    ← Back to Home
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3 md:space-y-4 mt-4 md:mt-0">
              <div className="text-center mb-3 md:mb-4">
                <h2 className="text-lg md:text-xl font-bold text-foreground font-heading">Reset Password</h2>
                <p className="text-xs md:text-sm text-muted-foreground mt-1.5 font-body">Enter your registered email address</p>
              </div>

              <div className="space-y-2.5 md:space-y-3">
                <div>
                  <label className="block text-xs md:text-sm font-medium mb-1.5 font-heading">Email Address *</label>
                  <div className="relative">
                    <input
                      type="email"
                      placeholder="Enter your registered email address"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 text-sm rounded-lg bg-secondary/80 border border-border focus:border-primary focus:outline-none pr-10 md:pr-12 font-body"
                    />
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    className={`btn-electric py-2.5 md:py-3 text-xs md:text-sm ${
                      forgotLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotEmail('');
                    }}
                    className="px-3 md:px-4 py-2.5 md:py-3 rounded-lg bg-secondary/80 border border-border hover:bg-secondary transition-colors font-body text-xs md:text-sm"
                  >
                    Cancel
                  </button>
                </div>

                <p className="text-xs text-muted-foreground text-center font-body">
                  We'll send password reset instructions to your email address
                </p>
              </div>

              <div className="text-center mt-3 md:mt-4 pt-3 md:pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="text-primary hover:text-primary/80 font-medium text-xs md:text-sm flex items-center justify-center gap-1.5 mx-auto font-body"
                >
                  ← Back to Login
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Login;